import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Alert,
  ScrollView,
  Modal,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Image,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";

const ManualVisitRedeem = ({ route, navigation }) => {
  // Extract CollabID from route.params
  const { CollabID } = route.params;

  const [collabDetails, setCollabDetails] = useState(null);

  // Original flow state
  const [venueCode, setVenueCode] = useState("");

  // Shared state
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [dates, setDates] = useState([]);

  // Original success/error modal (unchanged)
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Submit in-progress flag
  const [isSubmitting, setIsSubmitting] = useState(false);

  // NEW: Photo-flow states
  const [takePhotoModalVisible, setTakePhotoModalVisible] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [proofImageURL, setProofImageURL] = useState(""); // server URL after upload
  const [proofLocalPreview, setProofLocalPreview] = useState(null); // preview only

  // Hidden input (web) for camera/file capture
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchCollabDetails = async () => {
      try {
        const response = await fetch(
          `https://collabbay.com/APIControllers/GetManualRedeemDetails.php?CollabID=${CollabID}` // Use the dynamic CollabID
        );
        const data = await response.json();
        if (data.success) {
          setCollabDetails(data.details);
        } else {
          Alert.alert("Error", data.message);
        }
      } catch (error) {
        console.error("Error fetching collab details:", error);
      }
    };

    fetchCollabDetails(); // Fetch details using the dynamic CollabID
    generateDates(); // Generate date options
  }, [CollabID]); // Dependency added to re-run if CollabID changes

  const generateDates = () => {
    const today = new Date();
    const generatedDates = [];

    // Generate dates from today going backwards for 30 days
    for (let i = 0; i < 30; i++) {
      const date = new Date();
      date.setDate(today.getDate() - i); // Subtract days from today
      generatedDates.push(date.toDateString());
    }

    setDates(generatedDates); // No need to reverse, already in desired order
  };

  const handleBackPress = () => {
    navigation.goBack();
  };

  // ========= NEW: Photo Flow Handlers =========

  const openFilePickerWeb = () => {
    try {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } catch (e) {
      Alert.alert("Error", "Could not open camera/file picker.");
    }
  };

  const onPickedWebFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      Alert.alert("Invalid file", "Please choose an image.");
      return;
    }
    // Preview (works on web)
    const localURL = URL.createObjectURL(file);
    setProofLocalPreview({ uri: localURL });

    await uploadProofToServer(file);
  };

  const openNativeCamera = async () => {
    try {
      // Try to use expo-image-picker if available in native builds.
      const ImagePicker = require("expo-image-picker");
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== "granted") {
        Alert.alert("Permission required", "Camera permission is needed.");
        return;
      }
      const result = await ImagePicker.launchCameraAsync({
        quality: 0.7,
        base64: false,
        exif: false,
      });
      if (result?.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        setProofLocalPreview({ uri: asset.uri });

        // Convert to blob/file for upload
        const file = await uriToFile(asset.uri, `visit_${Date.now()}.jpg`);
        await uploadProofToServer(file);
      }
    } catch (err) {
      Alert.alert("Unavailable", "Camera not available in this build.");
    }
  };

  // Helper: convert local file URI to File (for fetch FormData)
  const uriToFile = async (uri, fileName) => {
    const res = await fetch(uri);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type || "image/jpeg" });
  };

  const uploadProofToServer = async (file) => {
    try {
      setUploadingProof(true);
      const form = new FormData();
      form.append("CollabID", CollabID);
      form.append("image", file);

      const res = await fetch(
        "https://collabbay.com/APIControllers/UploadVisitProof.php",
        {
          method: "POST",
          body: form,
        }
      );

      const data = await res.json();
      if (data?.success && data?.image_url) {
        setProofImageURL(data.image_url);
        setTakePhotoModalVisible(false);

        // UX: replace code input with a success flag
        setVenueCode(""); // clear if user had typed something
        Alert.alert(
          "Photo uploaded",
          "✅ Your on-site photo has been uploaded. Now select your visit date & time and press Confirm."
        );
      } else {
        throw new Error(data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      Alert.alert("Upload failed", "Could not upload the photo. Please try again.");
    } finally {
      setUploadingProof(false);
    }
  };

  // ========= SUBMIT HANDLER (dual-path) =========

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple clicks
    setIsSubmitting(true); // Disable button immediately

    // If photo flow is used, venueCode is NOT required.
    const needsCode = !proofImageURL;

    if ((needsCode && !venueCode) || !selectedDate || !selectedTime) {
      let missing = [];
      if (needsCode && !venueCode) missing.push("Venue Code");
      if (!selectedDate) missing.push("Date");
      if (!selectedTime) missing.push("Time");

      setModalMessage(`Please fill in: ${missing.join(", ")}`);
      setIsSuccess(false);
      setModalVisible(true);
      setIsSubmitting(false);
      return;
    }

    const payloadDateTime = `${selectedDate} ${selectedTime}`;

    try {
      // Decide endpoint based on whether user uploaded proof photo
      const endpoint = proofImageURL
        ? "https://collabbay.com/APIControllers/ManualInsertRedeemPhoto.php"
        : "https://collabbay.com/APIControllers/ManualInsertRedeem.php";

      const body = proofImageURL
        ? {
            CollabID: collabDetails.CollabID,
            DateTime: payloadDateTime,
            ProofImageURL: proofImageURL,
          }
        : {
            CollabID: collabDetails.CollabID,
            VenueCode: venueCode,
            DateTime: payloadDateTime,
          };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      // Messages tailored to branch
      const successMsg = proofImageURL
        ? "Visit logged with photo proof. Ensure your primary content is uploaded on time."
        : "Visit successfully logged. Ensure your primary content is uploaded on time.";

      const errorMsg = proofImageURL
        ? "Photo validation failed. Please try again."
        : "Invalid venue code. Ask venue staff for their 2-digit code.";

      setModalMessage(data.success ? successMsg : data.message || errorMsg);
      setIsSuccess(data.success);
      setModalVisible(true);

      if (data.success) {
        // Reset UI
        setVenueCode("");
        setSelectedDate("");
        setSelectedTime("");
        setProofImageURL("");
        setProofLocalPreview(null);
      } else {
        setIsSubmitting(false); // Allow retry if failed
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalMessage("An error occurred. Please try again later.");
      setIsSuccess(false);
      setModalVisible(true);
      setIsSubmitting(false); // Allow retry
    }
  };

  // ========= UI RENDERERS =========

  const renderDatePicker = () => (
    <ScrollView horizontal style={styles.datePickerContainer}>
      {dates.map((date, index) => (
        <TouchableOpacity
          key={index}
          style={[
            styles.dateOption,
            { backgroundColor: selectedDate === date ? "#FDF05D" : "white" },
          ]}
          onPress={() => setSelectedDate(date)}
        >
          <Text style={styles.dateOptionText}>{date}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderTimePicker = () => (
    <ScrollView style={styles.timeScrollView}>
      {Array.from({ length: 40 }, (_, i) => {
        const hour = Math.floor((i + 14) / 2) % 24;
        const minute = i % 2 === 0 ? "00" : "30";
        const ampm = hour >= 12 ? "PM" : "AM";
        const adjustedHour = hour % 12 === 0 ? 12 : hour % 12;
        const timeLabel = `${adjustedHour}:${minute} ${ampm}`;

        return (
          <TouchableOpacity
            key={i}
            style={[
              styles.timeSlot,
              selectedTime === timeLabel && styles.selectedTimeSlot,
            ]}
            onPress={() => setSelectedTime(timeLabel)}
          >
            <Text>{timeLabel}</Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  return (
    <View style={styles.page}>
      <View style={styles.container}>
        <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
          <Ionicons name="arrow-back" size={24} color="black" />
        </TouchableOpacity>

        {/* ======== BIG, PROMINENT PHOTO CTA AT TOP ======== */}
        <View style={styles.topCtaCard}>
          <View style={styles.topCtaHeader}>
            <Ionicons name="camera" size={18} color="black" />
            <Text style={styles.topCtaBadge}>NEW</Text>
          </View>
          <Text style={styles.topCtaTitle}>Take a picture to log your visit</Text>
          <Text style={styles.topCtaDesc}>
            Quick on-site logging. This is not content and doesn’t need to look pretty.
          </Text>
          {!proofImageURL ? (
            <TouchableOpacity
              style={styles.topCtaButton}
              onPress={() => setTakePhotoModalVisible(true)}
            >
              <Text style={styles.topCtaButtonText}>Open Camera & Upload Visit-proof</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.proofPillWrap}>
              <View style={styles.proofPill}>
                <Ionicons name="checkmark-circle" size={20} color="green" />
                <Text style={styles.proofPillText}>Photo uploaded as visit proof</Text>
              </View>
              {proofLocalPreview?.uri ? (
                <Image
                  source={{ uri: proofLocalPreview.uri }}
                  style={styles.previewImage}
                  resizeMode="cover"
                />
              ) : null}
              <TouchableOpacity
                onPress={() => {
                  setProofImageURL("");
                  setProofLocalPreview(null);
                }}
                style={styles.clearProofBtn}
              >
                <Text style={styles.clearProofBtnText}>Remove photo</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {collabDetails ? (
          <View style={styles.detailsContainer}>
            <Text style={styles.header}>
              or
              <Text style={{ color: "#fdf05d" }}>.</Text>
            </Text>
          </View>
        ) : (
          <Text style={styles.loadingText}>Loading collab details...</Text>
        )}

        {/* ========= Code input block (hidden if photo proof exists and you want to force photo-only) ========= */}
        {!proofImageURL && (
          <View style={styles.inputGroup}>
            <Text
              style={{
                backgroundColor: "#fdf05d",
                borderRadius: 20,
                padding: 20,
                textAlign: "center",
                width: "80%",
              }}
            >
              Or log using the 2-digit venue code (ask staff).
            </Text>

            <TextInput
              style={styles.inputVenueCode}
              value={venueCode}
              onChangeText={setVenueCode}
              keyboardType="numeric"
              maxLength={2}
              placeholder="_ _"
            />

            {/* Secondary link to photo flow (kept for parity) */}
            <TouchableOpacity
              onPress={() => setTakePhotoModalVisible(true)}
              style={styles.altButton}
            >
              <Text style={styles.altButtonText}>Prefer to take a quick photo instead?</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ========= Photo modal ========= */}
        <Modal
          visible={takePhotoModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setTakePhotoModalVisible(false)}
        >
          <View style={styles.takePhotoModalContainer}>
            <View style={styles.takePhotoModalContent}>
              <Text style={styles.takePhotoTitle}>No code? Log with a quick photo.</Text>
              <Text style={styles.takePhotoBody}>
                Don’t worry — you can just log your visit by taking a quick photo inside the
                venue. This is NOT content and doesn’t need to look pretty.
              </Text>

              {uploadingProof ? (
                <View style={{ alignItems: "center", marginTop: 10 }}>
                  <ActivityIndicator size="small" color="black" />
                  <Text style={{ marginTop: 10 }}>Uploading…</Text>
                </View>
              ) : (
                <>
                  <TouchableOpacity
                    style={styles.cameraBtn}
                    onPress={() => {
                      if (Platform.OS === "web") {
                        openFilePickerWeb();
                      } else {
                        openNativeCamera();
                      }
                    }}
                  >
                    <Ionicons name="camera" size={18} color="#000" />
                    <Text style={styles.cameraBtnText}>Take Photo</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.cancelBtn}
                    onPress={() => setTakePhotoModalVisible(false)}
                  >
                    <Text style={styles.cancelBtnText}>Cancel</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>

          {/* Hidden file input for WEB camera/file selection */}
          {Platform.OS === "web" && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={onPickedWebFile}
            />
          )}
        </Modal>

        {/* ========= Date/Time ========= */}
        <View style={styles.inputGroup}>
          <Text style={styles.label}>When did you visit?</Text>
        </View>

        {renderDatePicker()}
        {renderTimePicker()}

        {/* ========= Submit ========= */}
        <TouchableOpacity
          style={[styles.confButton, isSubmitting && { opacity: 0.5 }]}
          onPress={handleSubmit}
          disabled={isSubmitting}
        >
          <Text style={styles.buttonText}>
            {isSubmitting ? "Processing..." : "Confirm"}
          </Text>
        </TouchableOpacity>

        {/* ========= Result Modal (unchanged behavior) ========= */}
        <Modal
          visible={modalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setModalVisible(false)}
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalContent}>
              <Ionicons
                name={isSuccess ? "checkmark-circle" : "close-circle"}
                size={48}
                color={isSuccess ? "green" : "red"}
              />
              <Text style={styles.modalText}>{modalMessage}</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => {
                  setModalVisible(false);
                  if (isSuccess && typeof window !== "undefined") {
                    // Reload the page only on success
                    try {
                      window.location.reload();
                    } catch (e) {}
                  }
                }}
              >
                <Text style={styles.closeButtonText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      </View>
    </View>
  );
};

// ========= Styles =========
const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  container: {
    maxWidth: 380,
    margin: "auto",
    padding: 20,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
  },
  backButton: {
    position: "absolute",
    top: 20,
    left: 20,
    zIndex: 1,
    marginBottom: 30,
  },
  header: {
    textAlign: "center",
    fontSize: 24,
    marginBottom: 20,
    color: "black",
    fontWeight: "bold",
  },
  detailsContainer: {
    marginBottom: 20,
    padding: 10,
    marginTop: 30,
  },
  loadingText: {
    textAlign: "center",
  },

topCtaCard: {
    backgroundColor: "#FDF05D",     // FULL YELLOW
    borderRadius: 14,
    padding: 18,
    marginTop: 60,                  // PUSH DOWN BELOW BACK BUTTON
    marginBottom: 18,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    borderWidth: 0,
},

  topCtaHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  topCtaBadge: {
    marginLeft: "auto",
    backgroundColor: "black",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    fontWeight: "800",
    color: 'white',
    fontSize: 11,
  },
  topCtaTitle: {
    fontSize: 18,
    fontWeight: "800",
    color: "#000",
    marginBottom: 6,
  },
  topCtaDesc: {
    fontSize: 13,
    color: "#333",
    marginBottom: 12,
  },
topCtaButton: {
    backgroundColor: "#000",
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
    justifyContent: "center",
},

  topCtaButtonText: {
    color: "#fff",
    fontWeight: "700",
  },

  /* ======== Code block & shared UI ======== */
  inputGroup: {
    marginBottom: 15,
    justifyContent: "center",
    alignItems: "center",
  },
  label: {
    fontWeight: "bold",
    marginBottom: 7,
    textAlign: "center",
  },
  inputVenueCode: {
    padding: 15,
    fontSize: 55,
    textAlign: "center",
    marginTop: 17,
    backgroundColor: "#F2F2F2",
    marginBottom: 15,
    borderRadius: 10,
    width: "60%",
    maxWidth: 250,
    alignSelf: "center",
    borderWidth: 1,
    borderColor: "#EAEAEA",
  },

  /* ======== Date/Time ======== */
  datePickerContainer: {
    flexDirection: "row",
    marginBottom: 20,
  },
  dateOption: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginRight: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#CCC",
  },
  dateOptionText: {
    fontSize: 16,
  },
  timeScrollView: {
    height: 120,
    marginBottom: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EEE",
    backgroundColor: "#FAFAFA",
  },
  timeSlot: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderColor: "#EEE",
  },
  selectedTimeSlot: {
    backgroundColor: "#FDF05D",
  },

  /* ======== Buttons ======== */
  confButton: {
    backgroundColor: "black",
    borderRadius: 20,
    paddingVertical: 12,
    paddingHorizontal: 20,
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 20,
  },
  buttonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },

  /* ======== Result modal ======== */
  modalContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    padding: 20,
    borderRadius: 8,
    alignItems: "center",
    width: "80%",
  },
  modalText: {
    fontSize: 16,
    marginVertical: 15,
    textAlign: "center",
  },
  closeButton: {
    backgroundColor: "black",
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
    marginTop: 15,
  },
  closeButtonText: {
    color: "white",
    fontSize: 16,
    textAlign: "center",
    fontWeight: "bold",
  },

  // NEW styles for alt flow helper link
  altButton: {
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#DDD",
    backgroundColor: "#FAFAFA",
  },
  altButtonText: {
    fontSize: 14,
    textAlign: "center",
  },

  // Take-photo modal
  takePhotoModalContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
    backgroundColor: "rgba(0,0,0,0.35)",
  },
  takePhotoModalContent: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 20,
    width: "92%",
    maxWidth: 420,
  },
  takePhotoTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 8,
    textAlign: "center",
  },
  takePhotoBody: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 14,
  },
  cameraBtn: {
    backgroundColor: "#FDF05D",
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  cameraBtnText: {
    fontWeight: "600",
    marginLeft: 6,
  },
  cancelBtn: {
    marginTop: 10,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#EFEFEF",
  },
  cancelBtnText: {
    fontWeight: "600",
  },

  // Photo proof summary
  proofPillWrap: {
    alignItems: "center",
    marginTop: 6,
    marginBottom: 6,
  },
  proofPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#EAF7EA",
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "#CDEBCC",
  },
  proofPillText: {
    fontWeight: "600",
  },
  previewImage: {
    width: 180,
    height: 120,
    borderRadius: 8,
    marginTop: 10,
  },
  clearProofBtn: {
    marginTop: 8,
  },
  clearProofBtnText: {
    textDecorationLine: "underline",
  },
});

export default ManualVisitRedeem;
